import React from 'react'
import { Link } from 'react-router-dom'
import {
  Activity, Briefcase, ShieldCheck, Scale, PieChart, PenLine,
  Bell, FlaskConical, ArrowRight,
} from 'lucide-react'
import { PageHero, Section } from './_shared'

const FEATURES = [
  {
    id: 'streaming', icon: Activity, title: 'Live market streaming',
    body: 'Real-time LTP for your entire book via AngelOne SmartStream (or Kite Ticker), with NIFTY 50 & Bank Nifty indices and an automatic fallback when a feed drops.',
    points: ['Binary tick parsing', 'Circuit-breaker resilience', 'Index + equity in one stream'],
  },
  {
    id: 'portfolio', icon: Briefcase, title: 'Unified portfolio',
    body: 'Holdings across four broker accounts — Zerodha and AngelOne — aggregated into one view with live valuation, true cost basis and P&L that recompute on every tick.',
    points: ['4-account aggregation', 'Live LTP revaluation', 'Daily snapshots'],
  },
  {
    id: 'health', icon: ShieldCheck, title: 'Portfolio health',
    body: 'Every holding scored across multiple risk and quality factors, so concentration, drawdown and weakness surface before they cost you.',
    points: ['Multi-factor scoring', 'Concentration checks', 'Plain-language flags'],
  },
  {
    id: 'rebalance', icon: Scale, title: 'Risk-aware rebalancing',
    body: 'A state-of-the-art engine: shrinkage covariance, risk-parity sizing, no-trade bands and correlation-aware candidates — with honest before/after portfolio risk.',
    points: ['Shrinkage covariance', 'No-trade bands & turnover budget', 'Before/after risk'],
  },
  {
    id: 'funds', icon: PieChart, title: 'Fund intelligence',
    body: 'A universe of thousands of AMFI schemes filtered to the genuinely investable few, with category-relative scoring, full explainability and replacement audits.',
    points: ['Category-relative scoring', 'Confidence engine', 'HOLD / REDUCE / REPLACE'],
  },
  {
    id: 'notes', icon: PenLine, title: 'Knowledge workspace',
    body: 'A private thinking environment — rich notes, checklists, code, live-formula spreadsheets and file attachments — stored on disk, versioned, and entirely yours.',
    points: ['Live spreadsheet formulas', 'Inline rich text', '⌘K search · no database'],
  },
  {
    id: 'alerts', icon: Bell, title: 'Alerts & signals',
    body: 'Rule-based alerts over Telegram and email, market signals, and news ingestion for your holdings — so you’re informed without staring at screens.',
    points: ['Telegram + email', 'Custom rules', 'Holding news feed'],
  },
  {
    id: 'paper', icon: FlaskConical, title: 'Paper trading',
    body: 'Test strategies and the daily cycle against live prices with risk gates — no real capital, no KYC, all the learning.',
    points: ['Live-price simulation', 'Risk gates', 'Advisory only'],
  },
]

export default function Features() {
  return (
    <>
      <PageHero
        kicker="Features"
        title="Everything you need to run a portfolio with discipline."
        lede="One calm workspace that streams the market, unifies your accounts, researches funds, models risk and keeps your thinking — held to an institutional standard."
      >
        <Link to="/register" className="btn btn-primary inline-flex items-center gap-1.5">
          Get started <ArrowRight className="w-4 h-4" />
        </Link>
      </PageHero>

      <Section>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f) => (
            <div key={f.id} id={f.id} className="card scroll-mt-24 flex flex-col">
              <div className="w-11 h-11 rounded-xl bg-accent-50 flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-accent-600" />
              </div>
              <h3 className="font-semibold text-ink-900 text-[17px]">{f.title}</h3>
              <p className="mt-2 text-sm text-ink-500 leading-6 flex-1">{f.body}</p>
              <ul className="mt-4 space-y-1.5">
                {f.points.map((p) => (
                  <li key={p} className="flex items-center gap-2 text-[13px] text-ink-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-400 shrink-0" />{p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      <Section tone="quiet">
        <div className="text-center max-w-xl mx-auto">
          <h2 className="font-display font-semibold text-ink-900 text-2xl sm:text-3xl tracking-tight">
            See it on your own portfolio.
          </h2>
          <p className="mt-3 text-ink-500 leading-7">
            Connect your broker accounts and bring everything into one disciplined view.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/register" className="btn btn-primary inline-flex items-center gap-1.5">
              Create account <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/faq" className="btn btn-secondary">Read the FAQ</Link>
          </div>
        </div>
      </Section>
    </>
  )
}
